import React, { useState, useCallback } from "react";

const ReceiptSection = ({ order, photos, canUpload, onUploadComplete, children }) => {
    const [lightboxIndex, setLightboxIndex] = useState(null);

    const openLightbox = useCallback((idx) => {
        setLightboxIndex(idx);
    }, []);

    const closeLightbox = useCallback(() => {
        setLightboxIndex(null);
    }, []);

    const navigateLightbox = useCallback((direction) => {
        setLightboxIndex((prev) => {
            if (prev === null || !photos) return null;
            const next = prev + direction;
            if (next < 0) return photos.length - 1;
            if (next >= photos.length) return 0;
            return next;
        });
    }, [photos]);

    const handleLightboxKeyDown = useCallback((e) => {
        if (e.key === "Escape") closeLightbox();
        if (e.key === "ArrowLeft") navigateLightbox(-1);
        if (e.key === "ArrowRight") navigateLightbox(1);
    }, [closeLightbox, navigateLightbox]);

    const hasPhotos = photos && photos.length > 0;

    if (!hasPhotos && !canUpload) return null;

    return (
        <div className="receipt-section">
            <h4 className="receipt-section__title">Receipt Photos</h4>

            {hasPhotos && (
                <div className="receipt-section__gallery">
                    {photos.map((photo, idx) => (
                        <button
                            key={photo.id || idx}
                            className="receipt-section__thumb-btn"
                            onClick={() => openLightbox(idx)}
                            type="button"
                            aria-label={`View ${photo.photo_type === "signed_coc" ? "signed Chain of Custody document" : `photo ${idx + 1}`}`}
                        >
                            {photo.file_type === "pdf" || photo.file_type === "application/pdf" ? (
                                <div className="receipt-section__thumb-pdf">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                                        <path d="M14 2v6h6" />
                                    </svg>
                                    <span>PDF</span>
                                </div>
                            ) : (
                                <img
                                    src={photo.s3_url}
                                    alt={`Receipt photo ${idx + 1}`}
                                    className="receipt-section__thumb-img"
                                    loading="lazy"
                                />
                            )}
                            <span className={`receipt-section__thumb-badge ${photo.photo_type === "signed_coc" ? "receipt-section__thumb-badge--coc" : ""}`}>
                                {photo.photo_type === "signed_coc" ? "Signed Chain of Custody" : "Packaging Photo"}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {/* Upload area rendered via children (ReceiptUpload passed from parent) */}
            {canUpload && children}

            {/* Lightbox overlay */}
            {lightboxIndex !== null && hasPhotos && (
                <div
                    className="receipt-section__lightbox"
                    onClick={closeLightbox}
                    onKeyDown={handleLightboxKeyDown}
                    tabIndex={0}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Photo viewer"
                    ref={(el) => el && el.focus()}
                >
                    <div
                        className="receipt-section__lightbox-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="receipt-section__lightbox-close"
                            onClick={closeLightbox}
                            type="button"
                            aria-label="Close viewer"
                        >
                            &times;
                        </button>

                        {photos.length > 1 && (
                            <>
                                <button
                                    className="receipt-section__lightbox-nav receipt-section__lightbox-nav--prev"
                                    onClick={() => navigateLightbox(-1)}
                                    type="button"
                                    aria-label="Previous photo"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="15 18 9 12 15 6" />
                                    </svg>
                                </button>
                                <button
                                    className="receipt-section__lightbox-nav receipt-section__lightbox-nav--next"
                                    onClick={() => navigateLightbox(1)}
                                    type="button"
                                    aria-label="Next photo"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 18 15 12 9 6" />
                                    </svg>
                                </button>
                            </>
                        )}

                        {photos[lightboxIndex].file_type === "application/pdf" ? (
                            <div className="receipt-section__lightbox-pdf">
                                <p>PDF document</p>
                                <a
                                    href={photos[lightboxIndex].s3_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="receipt-section__lightbox-pdf-link"
                                >
                                    Open in new tab
                                </a>
                            </div>
                        ) : (
                            <img
                                src={photos[lightboxIndex].s3_url}
                                alt={`Receipt photo ${lightboxIndex + 1}`}
                                className="receipt-section__lightbox-img"
                            />
                        )}

                        <div className="receipt-section__lightbox-counter">
                            {lightboxIndex + 1} / {photos.length}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReceiptSection;
